import { useCallback, useState } from 'react';

import { IFeedbackModal } from 'components/layout/FeedbackModal';
import { displayToast } from 'components/system/Toast';
import { axiosRestApi } from 'config/axiosRestApi';
import { useFeedbackModal } from 'providers';
import { getAxiosTooManyRequestsFeedbackModalProps } from 'utils/getAxiosTooManyRequestsFeedbackModalProps';
import { logError } from 'utils/logError';
import { AUTH_STEP } from '../AuthManager/constants';
import { useAuthManagerContext } from '../AuthManager/Context/useAuthManagerContext';
import { AWAITING_VALIDATION_FEEDBACK } from './constants';

const EMAIL_VERIFICATION_ERROR_PROPS: IFeedbackModal = {
  type: 'error',
  title: 'Error sending email verification',
  message: 'Please try again later',
};

interface IEmailVerificationResponse {
  verified: boolean;
  validated: boolean;
}

const verifyEmailRequest = async () => {
  return await axiosRestApi.get<IEmailVerificationResponse>('/verify-email');
};

const resendVerificationRequest = async (email: string) => {
  return await axiosRestApi.post('/resend-verification', {
    email,
  });
};

export const useEmailVerification = () => {
  const [loading, setLoading] = useState(false);
  const { openFeedbackModal } = useFeedbackModal();
  const { setStep, prefilledEmail } = useAuthManagerContext();

  const handleBackToLogin = () => {
    setStep(AUTH_STEP.DEFAULT);
  };

  const sendEmailVerification = useCallback(async () => {
    try {
      const response = await resendVerificationRequest(prefilledEmail ?? '');
      if (response.status === 204) {
        displayToast({
          type: 'success',
          title: 'Email Sent',
          description: 'Please check your email to activate your account',
        });
      }
    } catch (error: unknown) {
      logError({ error });

      const modalProps =
        getAxiosTooManyRequestsFeedbackModalProps(error) ??
        EMAIL_VERIFICATION_ERROR_PROPS;

      openFeedbackModal(modalProps);
    }
  }, [openFeedbackModal, prefilledEmail]);

  const getEmailVerification = useCallback(async () => {
    try {
      const response = await verifyEmailRequest();
      if (!response.data.verified) {
        displayToast({
          type: 'error',
          title: 'Email not verified',
          description: 'Email still not verified, please check your email',
        });
        return;
      }

      if (!response.data.validated) {
        openFeedbackModal(AWAITING_VALIDATION_FEEDBACK);
        return;
      }

      setStep(AUTH_STEP.PHONE_VERIFICATION);
    } catch (error: unknown) {
      logError({ error });

      const modalProps =
        getAxiosTooManyRequestsFeedbackModalProps(error) ??
        EMAIL_VERIFICATION_ERROR_PROPS;

      openFeedbackModal(modalProps);
    } finally {
      setLoading(false);
    }
  }, [openFeedbackModal, setStep]);

  const handleResendEmail = async () => {
    await sendEmailVerification();
  };

  const handleAlreadyVerified = async () => {
    setLoading(true);
    await getEmailVerification();
  };

  return {
    handleAlreadyVerified,
    handleBackToLogin,
    handleResendEmail,
    loading,
    prefilledEmail,
  };
};
